import React, { createContext, useContext, useMemo, ReactNode, useCallback, useState, useEffect } from 'react';
import enTranslations from '@/locales/en.json';
import ptTranslations from '@/locales/pt.json';
import { supabase } from '@/integrations/supabase/client';

export interface I18nContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  currentLanguage: 'pt' | 'en';
  changeLanguage: (language: 'pt' | 'en') => Promise<void>;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<'pt' | 'en'>(() => {
    // Initialize with localStorage, fallback to browser language, then to English
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('preferred_language') as 'pt' | 'en';
      if (savedLanguage && (savedLanguage === 'pt' || savedLanguage === 'en')) {
        return savedLanguage;
      }
      return navigator.language.startsWith('pt') ? 'pt' : 'en';
    }
    return 'en';
  });

  // Load user's preferred language from profile when user changes
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferred_language')
            .eq('id', user.id)
            .single();
          
          if (profile?.preferred_language && 
              (profile.preferred_language === 'pt' || profile.preferred_language === 'en')) {
            setCurrentLanguage(profile.preferred_language);
            localStorage.setItem('preferred_language', profile.preferred_language);
          }
        }
      } catch (error) {
        console.error('Error loading user language preference:', error);
      }
    };

    loadUserLanguage();
  }, []);

  const changeLanguage = useCallback(async (language: 'pt' | 'en') => {
    setCurrentLanguage(language);
    
    // Save to localStorage
    localStorage.setItem('preferred_language', language);
    
    // Save to database if user is logged in
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ preferred_language: language })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Error saving language preference to database:', error);
    }
  }, []);

  const translations = useMemo(() => {
    return currentLanguage === 'pt' ? ptTranslations : enTranslations;
  }, [currentLanguage]);

  const t = useMemo(() => {
    return (key: string, params?: Record<string, string | number>): string => {
      const keys = key.split('.');
      let value: any = translations;

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          console.warn(`Translation key not found: ${key}`);
          return key; // Return the key if translation not found
        }
      }

      if (typeof value !== 'string') {
        console.warn(`Translation value is not a string: ${key}`);
        return key;
      }

      // Replace parameters if provided
      if (params) {
        return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
          return params[paramKey]?.toString() || match;
        });
      }

      return value;
    };
  }, [translations]);

  const contextValue = useMemo(() => ({
    t,
    currentLanguage,
    changeLanguage,
    isLoading: false
  }), [t, currentLanguage, changeLanguage]);

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    // Provide fallback context instead of throwing error
    console.warn('useI18n called outside provider, using fallback context');
    
    const fallbackContext: I18nContextType = {
      t: (key: string) => key, // Return key as fallback
      currentLanguage: 'en',
      changeLanguage: async () => {},
      isLoading: false
    };
    
    return fallbackContext;
  }
  return context;
};
